import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { IntlProvider } from "react-intl";
import { QueryClientProvider } from "@tanstack/react-query";
import {
  createRootRoute, createRoute, createRouter, lazyRouteComponent, Outlet, retainSearchParams, RouterProvider,
} from "@tanstack/react-router";
import { TooltipProvider } from "@responia/ui/components/tooltip";
import { DirectionProvider } from "@responia/ui/nova/direction";
import { TextosUIProvider } from "@responia/ui/nova/textos";
import "./index.css";
import { consultas } from "./datos";
import { aplicarIdioma, cargarCatalogo, DIRECCION, idiomaInicial, LOCALE, mensajes } from "./i18n";
import { PreferenciasProvider } from "./preferencias";
import { useT } from "./useT";
import { AppLayout } from "./shell/AppLayout";
import { Acceso } from "./pages/Acceso";

const raiz = createRootRoute({ component: Outlet });
const acceso = createRoute({ getParentRoute: () => raiz, path: "/acceso", component: Acceso });
// La sede elegida (?sede=) acompaña a la persona de una vista a otra.
const app = createRoute({
  getParentRoute: () => raiz, id: "app", component: AppLayout,
  validateSearch: (s: Record<string, unknown>): { sede?: string } => ({ sede: typeof s.sede === "string" && s.sede ? s.sede : undefined }),
  search: { middlewares: [retainSearchParams(["sede"])] },
});

// Cada vista es un trozo cargado a demanda: la primera pantalla solo paga lo que muestra.
// Rutas escritas una por una: TanStack Router tipa cada `<Link to>` a partir de ellas.
const hoy = createRoute({ getParentRoute: () => app, path: "/", component: lazyRouteComponent(() => import("./pages/Hoy"), "Hoy") });
const bandeja = createRoute({
  getParentRoute: () => app, path: "/bandeja", component: lazyRouteComponent(() => import("./pages/Bandeja"), "Bandeja"),
  // ?canal= abre la bandeja ya filtrada (los enlaces de las cifras de Hoy).
  validateSearch: (s: Record<string, unknown>): { ref?: string; canal?: string } => ({
    ref: typeof s.ref === "string" ? s.ref : undefined, canal: typeof s.canal === "string" ? s.canal : undefined,
  }),
});
const agenda = createRoute({
  getParentRoute: () => app, path: "/agenda", component: lazyRouteComponent(() => import("./pages/Agenda"), "Agenda"),
  validateSearch: (s: Record<string, unknown>): { vista?: "bahias"; dia?: string } => ({
    vista: s.vista === "bahias" ? "bahias" : undefined,
    dia: typeof s.dia === "string" && /^\d{4}-\d{2}-\d{2}$/.test(s.dia) ? s.dia : undefined,
  }),
});
const contactos = createRoute({
  getParentRoute: () => app, path: "/contactos", component: lazyRouteComponent(() => import("./pages/Contactos"), "Contactos"),
  validateSearch: (s: Record<string, unknown>): { c?: string } => ({ c: typeof s.c === "string" ? s.c : undefined }),
});
const campanas = createRoute({ getParentRoute: () => app, path: "/campanas", component: lazyRouteComponent(() => import("./pages/Campanas"), "Campanas") });
const analisis = createRoute({ getParentRoute: () => app, path: "/analisis", component: lazyRouteComponent(() => import("./pages/Analisis"), "Analisis") });
const asistente = createRoute({ getParentRoute: () => app, path: "/asistente", component: lazyRouteComponent(() => import("./pages/Asistente"), "Asistente") });
const automatizaciones = createRoute({ getParentRoute: () => app, path: "/automatizaciones", component: lazyRouteComponent(() => import("./pages/Automatizaciones"), "Automatizaciones") });
const integraciones = createRoute({ getParentRoute: () => app, path: "/integraciones", component: lazyRouteComponent(() => import("./pages/Integraciones"), "Integraciones") });
const ajustes = createRoute({
  getParentRoute: () => app, path: "/ajustes", component: lazyRouteComponent(() => import("./pages/Ajustes"), "Ajustes"),
  validateSearch: (s: Record<string, unknown>): { seccion?: string } => ({ seccion: typeof s.seccion === "string" ? s.seccion : undefined }),
});

const arbol = raiz.addChildren([
  acceso,
  app.addChildren([hoy, bandeja, agenda, contactos, campanas, analisis, asistente, automatizaciones, integraciones, ajustes]),
]);
// Les textes que les composants nova affichent d'eux-mêmes, dans la langue choisie.
function TextosNova({ children }: { children: React.ReactNode }) {
  const t = useT();
  return <TextosUIProvider value={{ cerrar: t("shell.cerrar") }}>{children}</TextosUIProvider>;
}

const router = createRouter({ routeTree: arbol, basepath: "/v2" });

declare module "@tanstack/react-router" {
  interface Register { router: typeof router }
}

await cargarCatalogo(idiomaInicial());
aplicarIdioma(idiomaInicial());

createRoot(document.getElementById("app")!).render(
  <StrictMode>
    <PreferenciasProvider>
      {(idioma) => (
        <IntlProvider locale={LOCALE[idioma]} messages={mensajes(idioma)} defaultLocale="es">
          <QueryClientProvider client={consultas}>
            {/* Radix fija dir="ltr" por defecto (Tabs, menús, selectores): aquí se le da la dirección del idioma. */}
            <DirectionProvider dir={DIRECCION(idioma)}>
              <TooltipProvider>
                <TextosNova>
                  <RouterProvider router={router} />
                </TextosNova>
              </TooltipProvider>
            </DirectionProvider>
          </QueryClientProvider>
        </IntlProvider>
      )}
    </PreferenciasProvider>
  </StrictMode>,
);
